import type { ProLayoutProps } from '@ant-design/pro-components';

/**
 * ProLayout 的 `token`（header / sider / pageContainer）配色。
 *
 * ⚠️ 为什么是「按主题给两套值」而不是写一组常量：
 *    ProLayout 的显式 token 值会**压过** `navTheme` 预设 —— 这正是
 *    此前「切暗色后左侧菜单仍是白底黑字」的根因（sider token 被硬编码成亮色）。
 *    因此这里把两套值都写出来，由 src/app.tsx 的 layout 运行时按**运行期主题**
 *    （utils/darkMode 的 store，不是 initialState —— 写 initialState 会让整棵树重渲染）
 *    选一套下发（写在 initialState.settings 之后，后写者胜）。
 *
 * 取值基准：dy-maritime-style/docs/prototype/system-prototype.html 第 16–148 行
 *    （`--sider-*` / `--topbar-*` 两组变量，亮暗成对），与 src/styles/tokens.css
 *    「主题语义色层」逐字一致 —— 改一处请同改另一处。
 *
 * 说明：这里刻意**不写 CSS 变量字符串**（如 'var(--sider-bg)'）：token 值会被
 *    antd 的 cssVar 管线当作颜色参与处理，传 var() 字符串的行为不可靠；两套字面值
 *    是确定性做法。CSS 侧（multi-level-menu.css）另有一层 var(--sider-*) 的
 *    !important 覆盖作为双保险，两者取值相同。
 */

/** 亮色一套（原型 :root） */
const LIGHT_TOKEN = {
  header: {
    colorBgHeader: '#FFFFFF',
    colorHeaderTitle: '#1A1D27',
    colorTextMenu: '#1A1D27',
    colorTextMenuSecondary: '#5A6070',
    colorTextMenuSelected: '#1B6FE8',
    colorBgMenuItemSelected: 'rgba(27, 111, 232, 0.10)',
    colorBgMenuItemHover: '#F4F5F8',
    colorBgRightActionsItemHover: '#F4F5F8',
  },
  sider: {
    colorMenuBackground: '#FFFFFF',
    colorMenuItemDivider: '#EAECF2',
    colorTextMenu: '#5A6070',
    colorTextMenuSecondary: '#8C93A3',
    colorTextMenuSelected: '#1B6FE8',
    colorBgMenuItemSelected: 'rgba(27, 111, 232, 0.10)',
    colorBgMenuItemHover: '#F2F3F7',
    colorTextMenuActive: '#1B6FE8',
    colorTextMenuItemHover: '#1B6FE8',
    colorTextSubMenuSelected: '#1B6FE8',
  },
  pageContainer: {
    paddingBlockPageContainerContent: 0,
    paddingInlinePageContainerContent: 0,
  },
};

/** 暗色一套（原型 :root.dark）——侧栏是深底，不是白底 */
const DARK_TOKEN = {
  header: {
    colorBgHeader: '#16171F',
    colorHeaderTitle: '#E6E8EF',
    colorTextMenu: '#E6E8EF',
    colorTextMenuSecondary: '#9BA2B3',
    colorTextMenuSelected: '#5B9DEF',
    colorBgMenuItemSelected: 'rgba(91, 157, 239, 0.18)',
    colorBgMenuItemHover: '#1F2230',
    colorBgRightActionsItemHover: '#1F2230',
  },
  sider: {
    colorMenuBackground: '#12131A',
    colorMenuItemDivider: '#262936',
    colorTextMenu: '#A6ACBE',
    colorTextMenuSecondary: '#6B7280',
    colorTextMenuSelected: '#FFFFFF',
    colorBgMenuItemSelected: 'rgba(91, 157, 239, 0.22)',
    colorBgMenuItemHover: 'rgba(255, 255, 255, 0.06)',
    colorTextMenuActive: '#5B9DEF',
    colorTextMenuItemHover: '#FFFFFF',
    colorTextSubMenuSelected: '#FFFFFF',
  },
  pageContainer: {
    paddingBlockPageContainerContent: 0,
    paddingInlinePageContainerContent: 0,
  },
};

/**
 * 取当前主题下的一套 ProLayout token。
 * @param darkMode 是否暗色（来自 initialState.darkMode / localStorage `umi_dark_mode`）
 */
export const getLayoutToken = (darkMode?: boolean) =>
  darkMode ? DARK_TOKEN : LIGHT_TOKEN;

/**
 * @name
 */
const Settings: ProLayoutProps & {
  pwa?: boolean;
  logo?: string;
} = {
  navTheme: 'light',
  // ERP 专业蓝（= styles/tokens.css 主题语义色层 --primary）
  colorPrimary: '#1B6FE8',
  layout: 'side',
  contentWidth: 'Fluid',
  fixedHeader: true,
  fixSiderbar: false,
  /*
   * 侧栏宽度：运行时由屏幕档位决定（A=216 / B=236 / C=56 / D=0），
   * 值取自 styles/tier-tokens.css 的 --tier-sider-w，在 src/app.tsx 的 runtime
   * layout 里用 readTierPx() 读出后写进 ProLayout（见 app.tsx「侧栏宽度必须写在
   * settings 之后」注释）。这里的 216 只是 A 档基准值 / 首帧兜底，
   * 不要在别处再硬编码第二份宽度。
   */
  siderWidth: 216,
  collapsed: false,
  colorWeak: false,
  title: 'LINSON MARINE',
  pwa: true,
  logo: '/logo.svg',
  iconfontUrl: '',
  // 首帧兜底用亮色一套；运行时由 app.tsx 按 darkMode 换成 getLayoutToken(true)
  token: LIGHT_TOKEN,
};

export default Settings;